/**
 * Fetches interview plan defaults for pre-filling the scheduling wizard.
 * GET /api_ats/v1/ats_interviews/new
 *
 * Used when scheduling from an interview plan (not adhoc).
 * Returns interviewer, duration, location, calendar invite templates, etc.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { assertResolvedAtsNotHttpError } from '@/api/ats/atsEnvelope';

export type InterviewPlanParams = {
  stageId: number;
  stageActivityId?: number;
  contactId?: number;
};

async function fetchInterviewPlan(
  params: InterviewPlanParams
): Promise<Record<string, unknown>> {
  let url = `ats_interviews/new?ats_job_stage_id=${params.stageId}`;
  if (params.stageActivityId) {
    url += `&ats_job_stage_action_id=${params.stageActivityId}`;
  }
  if (params.contactId) {
    url += `&contact_id=${encodeURIComponent(params.contactId)}`;
  }
  const res = await http.get(url);
  assertResolvedAtsNotHttpError(res);
  const data = (res as { data?: unknown })?.data;
  return (data && typeof data === 'object' ? data : res) as Record<
    string,
    unknown
  >;
}

export function useInterviewPlan(
  params: InterviewPlanParams,
  options?: { enabled?: boolean }
): UseQueryResult<Record<string, unknown>, Error> {
  return useQuery({
    queryKey: [
      'schedule-interview',
      'plan',
      params.stageId,
      params.stageActivityId,
      params.contactId,
    ],
    queryFn: () => fetchInterviewPlan(params),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
}
